import {
  SessionStateEvent,
  SessionStateEventType,
} from 'types/sessionStateLog';
import { getBrowserContext } from './browserContext';
import { SESSION_CONFIG, getAppVersion } from './config';

const MAX_RETRIES = 3;
const BACKOFF_MS = [500, 1500, 3000];
type UploadTarget = 'auth' | 'unauth';
type AuthState = 'authenticated' | 'unauthenticated' | 'unknown';

const MAX_EVENT_NAME_LENGTH = 128;
let authState: AuthState = 'unknown';

export const setSessionStateAuthState = (state: AuthState) => {
  authState = state;
};

const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const normalizeEventName = (value: string): string =>
  value.length > MAX_EVENT_NAME_LENGTH
    ? value.slice(0, MAX_EVENT_NAME_LENGTH)
    : value;

const sanitizeEvent = (event: SessionStateEvent): SessionStateEvent => {
  const eventName = normalizeEventName(event.eventName);
  const payload = event.eventPayload;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const eventPayload = payload as Record<string, unknown>;
    const payloadEventName = eventPayload['event_name'];
    if (typeof payloadEventName === 'string') {
      const normalizedPayloadName = normalizeEventName(payloadEventName);
      if (
        normalizedPayloadName !== payloadEventName ||
        eventName !== event.eventName
      ) {
        return {
          ...event,
          eventName,
          eventPayload: {
            ...eventPayload,
            event_name: normalizedPayloadName,
          },
        };
      }
    }
  }

  if (eventName === event.eventName) return event;
  return { ...event, eventName };
};

const withBackoff = async (
  batch: SessionStateEvent[],
  attempt: number = 0,
  target: UploadTarget = 'auth'
): Promise<void> => {
  const endpoint =
    target === 'unauth'
      ? '/api/session-state/events-unauth'
      : '/api/session-state/events';

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: target === 'auth' ? 'include' : 'omit',
      keepalive: true,
      body: JSON.stringify({ events: batch }),
    });
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      const error: Error & {
        retryable?: boolean;
        status?: number;
        responseBody?: string;
      } = new Error(body || `HTTP ${response.status}`);
      error.retryable = response.status >= 500;
      error.status = response.status;
      error.responseBody = body;
      throw error;
    }
  } catch (error) {
    const retryable =
      typeof (error as any)?.retryable === 'boolean'
        ? (error as any).retryable
        : true;
    if (!retryable) {
      throw error;
    }
    if (attempt >= MAX_RETRIES) {
      throw error;
    }
    const backoff =
      BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] +
      Math.floor(Math.random() * 200);
    await delay(backoff);
    return withBackoff(batch, attempt + 1, target);
  }
};

const chunkEvents = (events: SessionStateEvent[]): SessionStateEvent[][] => {
  const chunks: SessionStateEvent[][] = [];
  let current: SessionStateEvent[] = [];
  let currentSize = 0;

  for (const event of events) {
    const serialized = JSON.stringify(event);
    const size = serialized.length;

    const wouldOverflow =
      current.length >= SESSION_CONFIG.MAX_EVENTS_PER_BATCH ||
      currentSize + size > SESSION_CONFIG.MAX_BATCH_BYTES;

    if (wouldOverflow && current.length) {
      chunks.push(current);
      current = [];
      currentSize = 0;
    }

    current.push(event);
    currentSize += size;
  }

  if (current.length) {
    chunks.push(current);
  }

  return chunks;
};

export const sendSessionStateEvents = async (
  events: SessionStateEvent[],
  options?: { target?: UploadTarget }
): Promise<void> => {
  if (!events.length) return;
  const sanitized = events.map(sanitizeEvent);
  const enriched = sanitized.map((event) => ({
    ...event,
    appVersion: event.appVersion ?? getAppVersion(),
  }));

  const batches = chunkEvents(enriched);

  for (const batch of batches) {
    const inferredTarget: UploadTarget = (() => {
      if (options?.target) {
        return options.target;
      }
      if (authState === 'authenticated') {
        return 'auth';
      }
      if (authState === 'unauthenticated') {
        return 'unauth';
      }
      return 'unauth';
    })();

    const scrubbedBatch = batch.map((event) => ({
      ...event,
      userId: undefined,
    }));

    try {
      await withBackoff(scrubbedBatch, 0, inferredTarget);
    } catch (error) {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 401 && inferredTarget === 'auth') {
        setSessionStateAuthState('unauthenticated');
        await withBackoff(scrubbedBatch, 0, 'unauth');
      } else {
        throw error;
      }
    }
  }
};

export const recordBusinessAction = async (params: {
  sessionId: string;
  userId?: string | null;
  name: string;
  context?: Record<string, unknown>;
  severity?: SessionStateEvent['severity'];
  assignmentId?: string | null;
}) => {
  const {
    sessionId,
    userId,
    name,
    context,
    severity = 'INFO',
    assignmentId,
  } = params;
  const baseContext: Record<string, unknown> = {
    ...getBrowserContext({ assignmentId: assignmentId ?? undefined }),
    platform: 'web',
  };
  try {
    await sendSessionStateEvents([
      {
        sessionId,
        userId,
        clientTimestamp: new Date().toISOString(),
        platform: 'WEB',
        severity,
        eventType: SessionStateEventType.USER_FLOW_ACTION,
        eventName: name,
        eventPayload: {
          event_name: name,
          action_detail: context?.action_detail as string | undefined,
          source_context: context?.source_context as string | undefined,
          action_value: context?.action_value as string | undefined,
          expected_next_event: 'SYSTEM_FLOW',
          ...baseContext,
          ...context,
        },
      },
    ]);
  } catch {
    // business action logging should never break user flows
  }
};
